// src/modules/mapas/index.js — V2-R.2 Mapa Premium.
// 4 modos (Pins / Clusters / Heat / Geo) + popup rico + export PNG.

import { fetchMapaData, fetchPontosDoDia } from './queries.js';
import { uiIco } from '../../lib/uiIcons.js';
import { ensureLeaflet, ensureCluster, ensureHeat, ensureHtml2Canvas } from './leafletLoader.js';
import { tenant } from '../../context/tenantContext.js';
import { esc } from '../../lib/dom.js';
import { statusLabel, calcPctObra } from '../../lib/status.js';
import { loadingHTML } from '../../lib/loading.js';
import { showToast } from '../../lib/toast.js';
import { openModalNovoRDO } from '../obras/modalRDO.js';
import { mainDe, navigate } from '../../router/router.js';
import { setSubBar } from '../../components/sub-bar.js';
import './styles.css';
import { scAvColor } from '../../lib/scAvatar.js';

const STATE = {
  mode: 'pins',
  map: null,
  layers: {},
  obras: [],
  pontos: [],
  semCoord: [],
  rdos7d: [],
  kpisPorObra: {},
  data: null,
};

const CACHE_KEY = 'mapa-painel';

export async function render() {
  const main = mainDe('mapa');
  if (!main) return;
  main.innerHTML = loadingHTML('Carregando mapa…');

  STATE.data = new Date().toISOString().slice(0, 10);
  _setupSubBar();

  let cached = tenant.getCache(CACHE_KEY);
  if (!cached) {
    cached = await fetchMapaData(STATE.data);
    tenant.registerCache(CACHE_KEY, cached, 3 * 60 * 1000);
  }
  STATE.obras = cached.obras;
  STATE.semCoord = cached.semCoord;
  STATE.pontos = cached.pontos;
  STATE.rdos7d = cached.rdos7d;
  STATE.kpisPorObra = _computeKpis(STATE.obras, STATE.pontos, STATE.rdos7d);

  _renderShell(main);
  await _initMap();
  _bindListeners();
}

const MAP_ICO = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>';

// Sub-bar marker (INTELIGÊNCIA · Mapa de Obras · OBR-006) + date picker na área de ações
function _setupSubBar() {
  setSubBar({});
  try { window._subBarCtx = { id: 'OBR-006', pagina: 'Mapa de Obras', modulo: 'INTELIGÊNCIA' }; } catch (e) {}
}

// ── KPIs por obra ────────────────────────────────────────────────────
function _computeKpis(obras, pontos, rdos) {
  const out = {};
  obras.forEach((o) => {
    let pct = 0;
    if (o.etapas_obra?.length) {
      pct = calcPctObra(o.etapas_obra);
    } else if (o.status === 'concluida') pct = 100;
    const slug = (o.nome || '').toLowerCase().slice(0, 15);
    const equipeHoje = pontos.filter((p) =>
      (p.obra_detectada || '').toLowerCase().includes(slug)
    ).length;
    const rdos7d = rdos.filter((r) => r.obra_id === o.id).length;
    out[o.id] = {
      pct_executado: pct,
      equipe_hoje: equipeHoje,
      rdos_7d: rdos7d,
      prox_entrega: o.data_previsao_fim,
      valor_contrato: o.valor_contrato,
    };
  });
  return out;
}

// ── Shell HTML ──────────────────────────────────────────────────────
function _renderShell(main) {
  const _dark = !document.documentElement.classList.contains('light');
  main.innerHTML = `
    <div class="sc-scope ${_dark ? 'dark' : ''} cpsc" style="gap:12px">
    <div class="cpsc-head">
      <div><h2>Mapa de Obras</h2><p>Obras georreferenciadas e pontos do dia</p></div>
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
        <span class="sc-badge outline"><i class="sc-dot" style="background:var(--sc-info)"></i>${STATE.obras.length} obra(s)</span>
        <span class="sc-badge outline"><i class="sc-dot" style="background:var(--sc-ok)"></i><span id="mapa-pontos-count">${STATE.pontos.length} ponto(s) hoje</span></span>
        ${STATE.semCoord.length ? `<span class="sc-badge outline" style="color:var(--sc-warn)"><i class="sc-dot" style="background:var(--sc-warn)"></i>${STATE.semCoord.length} obra(s) sem GPS</span>` : ''}
        <input class="sc-input" type="date" id="mapa-data" value="${STATE.data}" max="${STATE.data}" style="width:160px;cursor:pointer">
      </div>
    </div>

    <div style="position:relative">
      <div id="mapa-leaflet" class="map-frame" style="height:calc(100vh - 260px);min-height:420px"></div>

      <div class="mp-toolbar">
        ${_modeBtn('pins',     'Pins',     '<path d="M12 2C7.6 2 4 5.6 4 10c0 5.5 8 12 8 12s8-6.5 8-12c0-4.4-3.6-8-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>')}
        ${_modeBtn('clusters', 'Clusters', '<circle cx="6" cy="6" r="3"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="18" r="3"/>')}
        ${_modeBtn('heat',     'Heat',     '<path d="M12 22c-4 0-7-2.7-7-6.5 0-2.5 1.6-4.7 4-7 .8-.7 1.8-2 2-3.5 1.5 2 4 4 4 6.5"/><path d="M14 22c1.8 0 3-1 3-2.5 0-1-.7-1.8-1.7-2.5"/>')}
        ${_modeBtn('geo',      'Geo',      '<circle cx="12" cy="12" r="9" stroke-dasharray="4 3"/><circle cx="12" cy="12" r="2" fill="currentColor"/>')}
      </div>

      <div class="mp-tools">
        <div class="mp-tool" data-action="png" title="Baixar imagem do mapa">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
          PNG
        </div>
      </div>
    </div>

    ${STATE.semCoord.length ? `
      <div class="section-card" style="margin-top:16px">
        <div class="section-card-title">Obras sem coordenadas</div>
        ${STATE.semCoord.map((o) => `
          <div style="display:flex;align-items:center;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--border)">
            <span>${esc(o.nome)}</span>
            <button class="btn btn-secondary" style="font-size:11px" data-edit-obra="${o.id}">Editar Obra →</button>
          </div>`).join('')}
      </div>` : ''}
  
    </div>`;
}

function _modeBtn(mode, label, paths) {
  const active = STATE.mode === mode ? 'active' : '';
  return `<div class="mp-mode ${active}" data-mode="${mode}">
    <svg viewBox="0 0 24 24" fill="${mode === 'pins' ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>
    ${label}
  </div>`;
}

// ── Inicialização do mapa ────────────────────────────────────────────
async function _initMap() {
  const L = await ensureLeaflet();
  const center = STATE.obras.length
    ? [STATE.obras[0].latitude, STATE.obras[0].longitude]
    : [-3.1190, -60.0217]; // Manaus default
  if (STATE.map) { STATE.map.remove(); STATE.map = null; }
  STATE.map = L.map('mapa-leaflet').setView(center, 13);

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    crossOrigin: 'anonymous',
    attribution: '© <a href="https://www.openstreetmap.org">OpenStreetMap</a>',
    maxZoom: 19,
  }).addTo(STATE.map);

  _applyThemeFilter();

  await _setMode(STATE.mode);

  STATE.layers.pontos = L.layerGroup().addTo(STATE.map);
  _renderPontosPins(STATE.pontos);

  // Fit bounds
  const allPoints = [
    ...STATE.obras.map((o) => [o.latitude, o.longitude]),
    ...STATE.pontos.filter((p) => p.latitude_entrada).map((p) => [p.latitude_entrada, p.longitude_entrada]),
  ];
  if (allPoints.length > 1) {
    STATE.map.fitBounds(L.latLngBounds(allPoints).pad(0.15));
  }
}

function _applyThemeFilter() {
  const el = document.getElementById('mapa-leaflet');
  if (!el) return;
  const isLight = document.documentElement.classList.contains('light');
  el.classList.toggle('map-dark', !isLight);
}

// ── Trocar modo ────────────────────────────────────────────────────
async function _setMode(mode) {
  const L = await ensureLeaflet();
  if (!STATE.map) return;
  STATE.mode = mode;
  document.querySelectorAll('.mp-mode').forEach((el) => {
    el.classList.toggle('active', el.dataset.mode === mode);
  });
  ['pins', 'cluster', 'heat', 'geo'].forEach((k) => {
    if (STATE.layers[k] && STATE.map.hasLayer(STATE.layers[k])) {
      STATE.map.removeLayer(STATE.layers[k]);
    }
  });

  if (!STATE.obras.length) return;

  if (mode === 'pins') {
    const group = L.layerGroup();
    STATE.obras.forEach((o) => {
      L.marker([o.latitude, o.longitude], { icon: _avatarIcon(L, o) })
        .bindPopup(_popupHTML(o), { maxWidth: 280, className: 'mp-rich' })
        .addTo(group);
    });
    STATE.layers.pins = group.addTo(STATE.map);
  } else if (mode === 'clusters') {
    await ensureCluster();
    const cluster = L.markerClusterGroup({
      iconCreateFunction: (c) => {
        const n = c.getChildCount();
        const tier = n < 5 ? 'mp-small' : n < 30 ? 'mp-medium' : 'mp-large';
        return L.divIcon({
          html: `<div><span>${n}</span></div>`,
          className: `marker-cluster ${tier}`,
          iconSize: L.point(40, 40),
        });
      },
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: 60,
    });
    STATE.obras.forEach((o) => {
      L.marker([o.latitude, o.longitude], { icon: _avatarIcon(L, o) })
        .bindPopup(_popupHTML(o), { maxWidth: 280, className: 'mp-rich' })
        .addTo(cluster);
    });
    STATE.layers.cluster = cluster.addTo(STATE.map);
  } else if (mode === 'heat') {
    await ensureHeat();
    const isLight = document.documentElement.classList.contains('light');
    const points = STATE.obras.map((o) => [o.latitude, o.longitude, 1.0]);
    STATE.layers.heat = L.heatLayer(points, {
      radius: isLight ? 38 : 32,
      blur: isLight ? 18 : 22,
      maxZoom: 17,
      max: 1.0,
      minOpacity: isLight ? 0.45 : 0.35,
      // Leaflet.heat pinta em canvas — var() não resolve. Lê os tokens e passa a cor real.
      gradient: (() => {
        const cs = getComputedStyle(document.documentElement);
        const t = (n, fb) => (cs.getPropertyValue(n).trim() || fb);
        const warn = t('--sc-warn', '#F59E0B'); const info = t('--sc-info', '#22D3EE'); const bad = t('--sc-bad', '#FF6467');
        return isLight
          ? { 0.2: warn, 0.4: warn, 0.6: info, 0.8: info, 1.0: bad }
          : { 0.2: warn, 0.4: warn, 0.6: warn, 0.8: bad, 1.0: bad };
      })(),
    }).addTo(STATE.map);
  } else if (mode === 'geo') {
    const pins = L.layerGroup();
    const geo = L.layerGroup();
    STATE.obras.forEach((o) => {
      L.marker([o.latitude, o.longitude], { icon: _avatarIcon(L, o) })
        .bindPopup(_popupHTML(o), { maxWidth: 280, className: 'mp-rich' })
        .addTo(pins);
      L.circle([o.latitude, o.longitude], { radius: 500, className: 'mp-geofence' })
        .bindTooltip(`Raio 500m · ${o.nome || ''}`, { direction: 'top', offset: [0, -10] })
        .addTo(geo);
    });
    STATE.layers.pins = pins.addTo(STATE.map);
    STATE.layers.geo = geo.addTo(STATE.map);
  }
}

// ── Avatar marker (sigla) ─────────────────────────────────────────
function _avatarIcon(L, o) {
  const color = _statusColor(o.status);
  const sigla = _sigla(o.nome);
  return L.divIcon({
    html: `<div style="background:${color};width:30px;height:30px;border-radius:50%;border:3px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;font-size:10px;font-family:'IBM Plex Mono',monospace;letter-spacing:-.02em">${sigla}</div>`,
    className: '',
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

function _statusColor(s) {
  return ({
    em_andamento: 'var(--sc-info)',
    planejamento: 'var(--sc-warn)',
    concluida: 'var(--sc-ok)',
    pausada: 'var(--sc-bad)',
    cancelada: 'var(--sc-idle)',
  })[s] || 'var(--sc-info)';
}

function _sigla(nome) {
  if (!nome) return 'OB';
  const parts = String(nome).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || 'OB').slice(0, 2).toUpperCase();
}

// ── Popup rico (com handlers via event delegation) ───────────────
function _popupHTML(o) {
  const k = STATE.kpisPorObra[o.id] || {};
  const stsLbl = statusLabel(o.status);
  const pct = Math.max(0, Math.min(100, Number(k.pct_executado || 0)));
  const pctClass = pct >= 80 ? 'green' : pct >= 50 ? 'amber' : 'red';

  const valorFmt = (v) => {
    if (!v) return '—';
    const n = Number(v);
    if (n >= 1e6) return 'R$ ' + (n / 1e6).toFixed(1) + 'M';
    if (n >= 1e3) return 'R$ ' + (n / 1e3).toFixed(0) + 'k';
    return 'R$ ' + n.toLocaleString('pt-BR');
  };
  const fmtD = (d) => {
    if (!d) return '—';
    try { return new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }); }
    catch { return d; }
  };

  const GREEN = 'var(--sc-ok)', AMBER = 'var(--sc-warn)';
  const stDot = o.status === 'concluida' ? GREEN : o.status === 'atrasada' ? 'var(--sc-bad)' : o.status === 'pausada' ? AMBER : 'var(--sc-info)';
  const barCor = pct >= 80 ? GREEN : pct >= 50 ? AMBER : 'var(--sc-bad)';
  const [avBg, avFg] = scAvColor(o.id || o.nome);
  return `<div class="sc-scope ${!document.documentElement.classList.contains('light') ? 'dark' : ''}" style="background:var(--background);color:var(--foreground);border:1px solid var(--border);border-radius:var(--radius-lg);padding:14px;min-width:250px;font-family:inherit">
    <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">
      <span style="width:32px;height:32px;border-radius:50%;display:inline-grid;place-items:center;font:600 12px/1 inherit;flex-shrink:0;background:${avBg};color:${avFg}">${_sigla(o.nome)}</span>
      <div style="min-width:0">
        <div style="font:600 14px/18px inherit">${esc(o.nome || '—')}</div>
        <div style="font:400 12px/16px inherit;color:var(--muted-foreground)">${esc(o.clientes?.nome_razao_social || o.endereco || '')}</div>
      </div>
    </div>
    <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px">
      <span class="sc-badge outline"><i class="sc-dot" style="background:${stDot}"></i>${esc(stsLbl)}</span>
      ${pct > 0 ? `<span class="sc-badge outline"><i class="sc-dot" style="background:var(--sc-info)"></i>${pct}% executado</span>` : ''}
    </div>
    <div class="sc-progress" style="height:6px;margin-bottom:10px"><i style="width:${pct}%;background:${barCor}"></i></div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:10px">
      ${[['Equipe hoje', k.equipe_hoje || 0], ['Próx entrega', fmtD(k.prox_entrega)], ['RDOs 7d', k.rdos_7d || 0], ['Contrato', valorFmt(k.valor_contrato)]].map(([l, v]) => `
        <div style="border:1px solid var(--border);border-radius:var(--radius-md);padding:7px 10px">
          <div style="font:500 10px/14px inherit;text-transform:uppercase;letter-spacing:.04em;color:var(--muted-foreground)">${l}</div>
          <div style="font:600 13px/18px inherit;font-variant-numeric:tabular-nums">${v}</div>
        </div>`).join('')}
    </div>
    <div style="display:flex;gap:8px">
      <button class="sc-btn sm" style="flex:1" data-pop-action="abrir" data-id="${o.id}">Abrir obra</button>
      <button class="sc-btn outline sm" data-pop-action="rdo" data-id="${o.id}">RDO</button>
    </div>
  </div>`;
}

// ── Pontos GPS dos funcionários ───────────────────────────────────
async function _renderPontosPins(pontos) {
  const L = await ensureLeaflet();
  if (!STATE.layers.pontos) return;
  STATE.layers.pontos.clearLayers();
  pontos.forEach((p) => {
    if (!p.latitude_entrada || !p.longitude_entrada) return;
    const dist = p.distancia_metros;
    const color = dist <= 100 ? 'var(--sc-ok)' : dist <= 300 ? 'var(--sc-warn)' : dist <= 500 ? 'var(--sc-warn)' : 'var(--sc-bad)';
    const nome = p.funcionarios?.nome || 'Funcionário';
    const cargo = p.funcionarios?.cargo || '';
    const icon = L.divIcon({
      html: `<div style="position:relative">
        <div style="background:${color};width:28px;height:28px;border-radius:50%;border:3px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;font-size:13px">👤</div>
        ${!p.saida ? '<div style="position:absolute;top:-2px;right:-2px;width:10px;height:10px;background:var(--sc-ok);border-radius:50%;border:2px solid #fff;animation:pulse-dot 1.5s infinite"></div>' : ''}
      </div>`,
      className: '',
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });
    L.marker([p.latitude_entrada, p.longitude_entrada], { icon })
      .addTo(STATE.layers.pontos)
      .bindPopup(
        `<div style="font-family:Inter,sans-serif;min-width:200px;padding:4px 0">
          <div style="font-size:14px;font-weight:700;color:#0E0E14;margin-bottom:2px">${esc(nome)}</div>
          ${cargo ? `<div style="font-size:11px;color:var(--sc-idle);margin-bottom:6px">${esc(cargo)}</div>` : ''}
          <div style="display:grid;grid-template-columns:auto 1fr;gap:3px 8px;font-size:12px">
            <span style="color:var(--sc-idle)">Entrada</span><span style="font-weight:600">${p.entrada ? p.entrada.slice(0, 5) : '—'}</span>
            ${p.saida ? `<span style="color:var(--sc-idle)">Saída</span><span style="font-weight:600">${p.saida.slice(0, 5)}</span>` : ''}
            <span style="color:var(--sc-idle)">Obra</span><span style="font-weight:600">${esc(p.obra_detectada || '—')}</span>
            <span style="color:var(--sc-idle)">Distância</span><span style="font-weight:600;color:${color}">${dist != null ? (dist < 1000 ? dist + 'm' : (dist / 1000).toFixed(1) + 'km') : '—'}</span>
          </div>
          <div style="margin-top:8px;padding-top:6px;border-top:1px solid #ECECEE;font-size:11px;color:${p.saida ? 'var(--sc-ok)' : 'var(--sc-warn)'}">${p.saida ? '✓ Saída registrada' : '○ Ainda no canteiro'}</div>
        </div>`,
        { maxWidth: 260 }
      );
  });
  const el = document.getElementById('mapa-pontos-count');
  if (el) el.textContent = `${pontos.length} ponto(s)`;
}

// ── Export PNG ────────────────────────────────────────────────────
async function _exportPNG() {
  const h2c = await ensureHtml2Canvas();
  const el = document.getElementById('mapa-leaflet');
  if (!el) { showToast('Mapa não disponível.', 'error'); return; }
  showToast('Gerando imagem…', 'info');
  try {
    const canvas = await h2c(el, { useCORS: true, allowTaint: false, scale: 2 });
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mapa_' + new Date().toISOString().slice(0, 10) + '.png';
    document.body.appendChild(a); a.click(); a.remove();
    showToast('Imagem do mapa baixada!');
  } catch (err) {
    console.error('[mapa] PNG falhou:', err);
    showToast('Falha ao gerar imagem.', 'error');
  }
}

// ── Listeners ────────────────────────────────────────────────────
function _bindListeners() {
  document.querySelectorAll('.mp-mode').forEach((el) => {
    el.addEventListener('click', () => _setMode(el.dataset.mode));
  });
  const png = document.querySelector('[data-action="png"]');
  if (png) png.addEventListener('click', _exportPNG);

  const inp = document.getElementById('mapa-data');
  if (inp) inp.addEventListener('change', async (e) => {
    const d = e.target.value;
    if (!d) return;
    const pts = await fetchPontosDoDia(d);
    _renderPontosPins(pts);
  });

  document.querySelectorAll('[data-edit-obra]').forEach((b) => {
    b.addEventListener('click', () => navigate('obra', { id: b.dataset.editObra }));
  });

  // popup action delegation
  document.addEventListener('click', _popupClick);
}

function _popupClick(e) {
  const btn = e.target.closest('[data-pop-action]');
  if (!btn) return;
  const action = btn.dataset.popAction;
  if (action === 'abrir') {
    navigate('obra', { id: btn.dataset.id });
  } else if (action === 'rdo') {
    if (btn.dataset.id) openModalNovoRDO(btn.dataset.id);
    else showToast('Obra não identificada.', 'error');
  }
}
